/**
 * Line labels and the active-line glow (W2-02, W3-06; DESIGN.md §4.8): "#3 Main" next to the lines Phira draws
 * (labels toggle), the active line glowing in the accent colour with a bold label (always in Edit mode). Invisible
 * lines get their labels from invisibleLines.ts.
 *
 * Labels never pile up (UX-052): each one takes the first free spot around its line (right above, right below, left
 * above, left below) clear of every label drawn before it in the frame (HUD outlines, invisible lines), and is left
 * out when none is free. The active line is labelled first and always; then the hovered and the selected lines;
 * the other lines only while at most `DENSE_LINES` lines are visible. A "+N labels hidden" note counts the rest
 * (hover a line to see its label).
 */

import type { AppServices } from '../../../app/registry/types';
import type { LineStateRt } from '../../../core/engine/types';
import type { LineId } from '../../../core/model/types';
import { PREVIEW_FRAME_ID, type OverlayDrawContext, type PreviewOverlayProvider } from '../../../render/types';
import { filteredOut } from '../filters';
import { m } from '../preview.messages';
import {
  dp,
  drawLabel,
  lineLabel,
  lineShape,
  linesAt,
  measureLabel,
  placeLabel,
  shapeCentre,
  strokeShape,
} from './shared';
import { overlayTheme } from './theme';

/** Above this many visible lines only the active, hovered and selected lines are labelled. */
export const DENSE_LINES = 6;
/** Most labels drawn per frame (the active line always gets its own). */
const MAX_LABELS = 60;

/** Lines to label, most important first, and how many visible lines are left out by the density rule. */
export function labelPlan(
  visible: readonly LineStateRt[],
  active: LineId | null,
  hovered: LineId | null,
  selected: ReadonlySet<LineId>,
): { readonly order: readonly LineStateRt[]; readonly skipped: number } {
  const rank = (s: LineStateRt): number =>
    s.line === active ? 0 : s.line === hovered ? 1 : selected.has(s.line) ? 2 : 3;
  const dense = visible.length > DENSE_LINES;
  const order = visible
    .filter((s) => !dense || rank(s) < 3)
    .sort((a, b) => rank(a) - rank(b) || a.index - b.index)
    .slice(0, MAX_LABELS);
  return { order, skipped: visible.length - order.length };
}

export function createLabelsOverlay(services: AppServices): PreviewOverlayProvider {
  /** The line under the pointer (labelled even on a crowded stage). */
  let hovered: LineId | null = null;
  return {
    id: 'preview.labels',
    order: 50,
    draw(c: OverlayDrawContext) {
      const st = services.editor.getState();
      const store = services.store;
      const doc = store?.doc ?? null;
      const idx = store?.idx ?? null;
      const theme = overlayTheme();
      const ctx = c.ctx;
      // Active line glow.
      const active = st.activeLine;
      const activeState = active === null ? undefined : c.lines.find((s) => s.line === active);
      if (activeState !== undefined && activeState.drawsNothing === null) {
        const shape = lineShape(c, activeState, doc, idx);
        ctx.save();
        ctx.globalAlpha = 0.35;
        strokeShape(c, shape, theme.accent, 7, false);
        ctx.restore();
      }
      const label = (s: LineStateRt, force: boolean): boolean => {
        const [x, y] = shapeCentre(lineShape(c, s, doc, idx));
        const isActive = s.line === active;
        const strong = isActive || s.line === hovered || st.selection.lines.has(s.line);
        const name = doc?.lines.get(s.line)?.name ?? '';
        const box = placeLabel(
          c,
          lineLabel(s.index, name),
          x,
          y,
          {
            fg: strong ? theme.text : theme.textMuted,
            bg: theme.surface,
            ...(isActive
              ? { border: theme.accent, bold: true }
              : st.selection.lines.has(s.line)
                ? { border: theme.selection }
                : s.line === hovered
                  ? { border: theme.hover }
                  : {}),
          },
          force,
        );
        return box !== null;
      };
      if (!st.preview.lineLabels) {
        if (activeState !== undefined && activeState.drawsNothing === null) label(activeState, true);
        return;
      }
      const visible = c.lines.filter((s) => s.drawsNothing === null && !filteredOut(st.filters, s.line));
      const plan = labelPlan(visible, active, hovered, st.selection.lines);
      let hidden = plan.skipped;
      for (const s of plan.order) if (!label(s, s.line === active)) hidden++;
      if (hidden > 0) {
        const text = services.i18n.t(m.labelsHidden, { count: hidden });
        const style = { fg: theme.textMuted, bg: theme.surface, border: theme.border, size: 10 };
        const [w, h] = measureLabel(c, text, style);
        drawLabel(c, text, (c.ctx.canvas.width - w) / 2, c.ctx.canvas.height - h - dp(c, 6), style);
      }
    },
    onPointerMove(c, e) {
      if (e.buttons !== 0) return;
      const st = services.editor.getState();
      if (!st.preview.lineLabels) return;
      const store = services.store;
      const top =
        linesAt(c, e.px, e.py, {
          doc: store?.doc ?? null,
          idx: store?.idx ?? null,
          invisible: false,
          skip: (line) => filteredOut(st.filters, line),
        })[0] ?? null;
      if (top === hovered) return;
      hovered = top;
      services.scheduler.request(PREVIEW_FRAME_ID);
    },
  };
}
